// What the piece on a square is called.
//
// `PIECE_NAMES` answers what a *rank* is called, which is the right question
// for a recipe — a foundry saving for a knight has no square to ask about. It
// is the wrong question for a piece standing on the board, because two things
// on this board are not named by their rank:
//
//   - an **Empress**, a queen Coronation raised to royalty. She is a `KING` in
//     every respect the rules care about, so `PIECE_NAMES` could only answer
//     "King", which loses the whole of what the player did.
//   - a **Regent**, the same crown on any other rank since §26 C5 — a crowned
//     rook is not an empress. The word `regents` already uses for a spare royal.
//   - a **variant** — a Marshal, a Drone-Assisted Miner, a **Matriarch**. It
//     keeps its rank, and unlike the two above the rank is not a thing that
//     happened to it: the row *is* what the piece is, so it supplies its own
//     name and there is no branch here for any of them.
//
// **Matriarch and Empress are deliberately different words for deliberately
// different things**, and keeping them apart is the point of this file:
// Matriarch is a queen who took a **row** — royal, a foundry, still a `QUEEN`
// — and Empress is a queen who took a **crown** and stopped being one. An army
// can hold both at once, on two different queens, and a player reading the
// board has to be able to tell which is which.
//
// One function so they cannot drift: a badge, the shelf, a refusal string and a
// snapshot all name the same piece the same way, and adding the next such piece
// is one entry here rather than a search for every place that spells a rank.

import { pieceType, type PieceType, KING, QUEEN } from "./board";
import { crownedFrom, pieceAt, type Position, variantAt } from "./position";
import { PIECE_NAMES, pieceVariant } from "./rules";

/** A queen raised to royalty by Coronation. */
export const EMPRESS = "Empress";
/** Coronation's crown on any other rank — a rook, a bishop, a knight. */
export const REGENT = "Regent";
// **No `MATRIARCH` here.** She is a `PIECE_VARIANTS` row, so the table owns
// the word and a second copy of it is one to keep in step.

/**
 * What to call the piece standing here.
 *
 * **The position is the whole answer**, and it took the Matriarch becoming a
 * row to make that true: she was a flag in `IndustryState`, so this took an
 * optional industry and every caller without one got "Queen" — accurate about
 * her rank, quiet about her role, and the right way to be wrong only because
 * there was no better one available.
 */
export function pieceLabel(position: Position, square: number): string {
  const piece = pieceAt(position, square);
  if (!piece) return "";
  const type = pieceType(piece);

  // Royal first, because it is a change of *rank* and a row is not: a crowned
  // queen is not a queen any more.
  //
  // The stored rank picks the word, so there is no second table to keep in step.
  const crown = crownedFrom(position, square);
  if (type === KING && crown) return crown === QUEEN ? EMPRESS : REGENT;

  // A crown is a thing that *happened to* a rank; a variant is the piece's own
  // row, so it names itself and `PIECE_NAMES` adds nothing. `MATRIARCH` is the
  // row's own `name` now, which is why there is no branch here for her.
  const variant = variantAt(position, square);
  if (variant) return pieceVariant(variant).name;
  return PIECE_NAMES[type] ?? "?";
}

/**
 * The short form, for a badge with no room for "Drone-Assisted Miner". Falls
 * through to `pieceLabel`, so a caller never has to know which rows have one.
 */
export function shortPieceLabel(position: Position, square: number): string {
  const variant = variantAt(position, square);
  return variant ? pieceVariant(variant).short : pieceLabel(position, square);
}

/**
 * The same answer for a rank with no square behind it — a recipe, a table row.
 *
 * Exported so a caller that means "the rank" says so, rather than reaching past
 * this file to `PIECE_NAMES` and quietly becoming a place an Empress is called
 * a King.
 */
export const rankLabel = (type: PieceType): string => PIECE_NAMES[type] ?? "?";

/**
 * What a piece *becomes* by climbing to this rung, for an option a player picks.
 *
 * `KING` is the only rung whose name is not its rank: the ladder puts it above
 * `QUEEN` and nowhere else, so an upgrade to a crown is always a queen taking
 * one — an **Empress**. Offering it as "King" describes the mechanism instead
 * of the outcome, and is the same conflation this file exists to settle.
 */
export const upgradeLabel = (target: PieceType): string =>
  target === KING ? EMPRESS : rankLabel(target);
